import type {
  MaiConfig,
  MaiCommand,
  MaiPixelFunction,
  CustomerData,
  ConsentData,
  EventParams,
} from "./types";
import { configManager, log } from "./config";
import { getOrCreateClientId } from "./client-id";
import { isBot } from "./bot";
import { sendEvent } from "./transport";
import { buildEventPayload } from "./events";
import { initUtmTracking } from "./utm";

export type {
  MaiConfig,
  MaiCommand,
  MaiPixelFunction,
  CustomerData,
  ConsentData,
};
export type {
  ProductViewedParams,
  ProductAddedToCartParams,
  ProductRemovedFromCartParams,
  CartViewedParams,
  SearchSubmittedParams,
  CollectionViewedParams,
  CheckoutParams,
  CheckoutCompletedParams,
  ItemData,
  ExtraParams,
} from "./types";

/** 必填参数校验规则 */
const REQUIRED_PARAMS: Record<string, string[]> = {
  product_viewed: ["product_id", "product_variant_id"],
  product_added_to_cart: ["product_id", "product_variant_id", "quantity"],
  product_removed_from_cart: ["product_id", "product_variant_id"],
  checkout_completed: ["order_id"],
};

function validateRequiredParams(command: string, params?: EventParams): void {
  const required = REQUIRED_PARAMS[command];
  if (!required) return;

  const missing = required.filter(
    (key) => !params || params[key] === undefined || params[key] === ""
  );
  if (missing.length > 0) {
    console.warn(
      `[MAI SDK] ${command} missing recommended params: ${missing.join(", ")}`
    );
  }
}

const EVENT_COMMANDS = new Set<string>([
  "page_viewed",
  "product_viewed",
  "product_added_to_cart",
  "product_removed_from_cart",
  "cart_viewed",
  "search_submitted",
  "collection_viewed",
  "checkout_started",
  "checkout_completed",
]);

/** 处理单条命令 */
function processCommand(command: string, data?: unknown): string | void {
  // 工具命令
  if (command === "setCustomer") {
    configManager.setCustomer(data as CustomerData);
    log("Customer updated:", data);
    return;
  }

  if (command === "consent") {
    configManager.setConsent(data as boolean | ConsentData);
    log(
      "Consent updated:",
      configManager.consent,
      "tracking:",
      configManager.trackingEnabled
    );

    // 发送 consent 变化事件，与 Web Pixel 的 _customer_attributes_changed 对齐
    const consentPayload = buildEventPayload("_customer_attributes_changed");
    consentPayload.attribute_change = {
      attribute_name: "consent",
      attribute_value: configManager.consent,
    };
    sendEvent(consentPayload);
    return;
  }

  if (command === "getClientId") {
    return getOrCreateClientId();
  }

  // 事件命令
  if (EVENT_COMMANDS.has(command)) {
    // consent(false) 后停止发送
    if (!configManager.trackingEnabled) {
      log("Event blocked by consent:", command);
      return;
    }

    // Bot 过滤
    if (isBot()) {
      log("Event blocked (bot detected):", command);
      return;
    }

    // 推荐参数校验（warn but still send）
    const params = data as EventParams | undefined;
    validateRequiredParams(command, params);

    const payload = buildEventPayload(command, params);
    sendEvent(payload);

    return payload.event_id;
  }

  log("Unknown command:", command);
}

/** 初始化 SDK */
export function initMaiPixel(config?: MaiConfig) {
  // 防止重复初始化
  if (configManager.initialized) {
    log("SDK already initialized, skipping.");
    return;
  }

  const finalConfig = config || window.MAI_CONFIG;
  if (!finalConfig) {
    console.error(
      "[MAI SDK] Missing config. Set window.MAI_CONFIG or pass config to initMaiPixel()."
    );
    return;
  }

  if (!finalConfig.storeDomain) {
    console.error("[MAI SDK] storeDomain is required.");
    return;
  }

  configManager.init(finalConfig);
  window.MAI_CONFIG = finalConfig;
  log("Initializing, version:", configManager.version);

  // 初始化 client ID
  getOrCreateClientId();

  // UTM 追踪
  initUtmTracking();

  // 排空队列
  const queue = window.MaiPixel?.q;
  if (queue && queue.length > 0) {
    log(`Draining ${queue.length} queued commands`);
    for (const args of queue) {
      processCommand(args[0] as string, args[1]);
    }
    queue.length = 0;
  }

  // 替换为正式函数
  MaiPixel._initialized = true;
  window.MaiPixel = MaiPixel;

  // 自动触发首次 page_viewed
  processCommand("page_viewed");

  log("SDK initialized successfully!");
}

/**
 * MaiPixel — Main tracking function.
 *
 * Can be used via ESM import or `window.MaiPixel` (equivalent).
 *
 * @example
 * import { MaiPixel } from '@mai-co/pixel'
 * MaiPixel('product_viewed', { product_id: '123', product_variant_id: '456' })
 */
export const MaiPixel = ((command: MaiCommand, data?: unknown) => {
  if (!configManager.initialized) {
    // SDK 未初始化时，把命令加入队列
    MaiPixel.q = MaiPixel.q || [];
    MaiPixel.q.push([command, data]);
    return;
  }
  return processCommand(command, data);
}) as MaiPixelFunction;

// IIFE 模式下自动初始化
if (typeof window !== "undefined" && window.MAI_CONFIG) {
  if (document.readyState !== "loading") {
    initMaiPixel();
  } else {
    document.addEventListener("DOMContentLoaded", () => initMaiPixel());
  }
}
